class FlavorSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === "true") return;

    this.toggleButton = this.querySelector(".flavor-selector__toggle");
    this.list = this.querySelector(".flavor-selector__list");
    this.options = Array.from(this.querySelectorAll("[data-flavor-option]"));

    if (!this.toggleButton || !this.list || this.options.length < 2) return;

    this.dataset.initialized = "true";
    this.onToggleClick = this.handleToggleClick.bind(this);
    this.onToggleKeydown = this.handleToggleKeydown.bind(this);
    this.onListKeydown = this.handleListKeydown.bind(this);
    this.onOptionClick = this.handleOptionClick.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onPopState = this.handlePopState.bind(this);

    this.toggleButton.addEventListener("click", this.onToggleClick);
    this.toggleButton.addEventListener("keydown", this.onToggleKeydown);
    this.list.addEventListener("keydown", this.onListKeydown);
    this.list.addEventListener("click", this.onOptionClick);
    this.addEventListener("focusout", this.onFocusOut);
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onDocumentKeydown);
    window.addEventListener("popstate", this.onPopState);

    this.setOpen(false);
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener("click", this.onToggleClick);
    this.toggleButton?.removeEventListener("keydown", this.onToggleKeydown);
    this.list?.removeEventListener("keydown", this.onListKeydown);
    this.list?.removeEventListener("click", this.onOptionClick);
    this.removeEventListener("focusout", this.onFocusOut);
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onDocumentKeydown);
    window.removeEventListener("popstate", this.onPopState);
    delete this.dataset.initialized;
  }

  handleToggleClick() {
    this.setOpen(this.list.hidden);
  }

  handleToggleKeydown(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

    event.preventDefault();
    this.setOpen(true);
    const option = event.key === "ArrowUp" ? this.options.at(-1) : this.options[0];
    option?.focus();
  }

  handleListKeydown(event) {
    const currentIndex = this.options.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex;

    switch (event.key) {
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % this.options.length;
        break;
      case "ArrowUp":
        nextIndex = (currentIndex - 1 + this.options.length) % this.options.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = this.options.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.options[nextIndex]?.focus();
  }

  handleOptionClick(event) {
    const option = event.target.closest("[data-flavor-option]");
    if (!option || !this.contains(option)) return;

    if (option.getAttribute("aria-current") === "page") {
      event.preventDefault();
      this.setOpen(false);
      return;
    }

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const destination = new URL(option.href, window.location.href);
    if (destination.origin !== window.location.origin) return;

    event.preventDefault();
    this.navigateTo(destination, {
      updateHistory: true,
      pageBackground: option.dataset.pageBackground,
    });
  }

  handlePopState() {
    const option = this.options.find((item) => {
      const optionUrl = new URL(item.href, window.location.href);
      return optionUrl.pathname === window.location.pathname;
    });

    if (!option) return;

    this.navigateTo(new URL(window.location.href), {
      updateHistory: false,
      pageBackground: option.dataset.pageBackground,
    });
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) this.setOpen(false);
  }

  handleDocumentKeydown(event) {
    if (event.key !== "Escape" || this.list.hidden) return;

    this.setOpen(false);
    this.toggleButton.focus();
  }

  handleFocusOut() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.setOpen(false);
    });
  }

  setOpen(isOpen) {
    this.list.hidden = !isOpen;
    this.toggleButton.setAttribute("aria-expanded", String(isOpen));
    this.toggleAttribute("data-open", isOpen);
  }

  async navigateTo(destination, { updateHistory, pageBackground } = {}) {
    if (FlavorSelector.isNavigating) return;

    FlavorSelector.isNavigating = true;
    this.setOpen(false);
    this.toggleAttribute("data-loading", true);
    this.setAttribute("aria-busy", "true");

    const transitionColor =
      pageBackground || getComputedStyle(document.body).backgroundColor;
    const transitionPromise = this.coverPage(transitionColor);

    try {
      const response = await fetch(destination.href, {
        credentials: "same-origin",
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);

      const responseText = await response.text();
      const responseDocument = new DOMParser().parseFromString(
        responseText,
        "text/html"
      );
      const nextMain = responseDocument.querySelector("#MainContent");
      const currentMain = document.querySelector("#MainContent");

      if (!nextMain || !currentMain) {
        throw new Error("The fetched product page is missing its main content");
      }

      const transition = await transitionPromise;
      this.removeDetachedModals(currentMain);
      this.destroyCurrentSliders(currentMain);

      const importedMain = document.importNode(nextMain, true);
      currentMain.replaceWith(importedMain);

      this.syncProductTheme(responseDocument);
      this.syncDocumentMetadata(responseDocument, destination, updateHistory);
      this.initializePageContent(importedMain);

      await this.revealPage(transition);
      importedMain
        .querySelector("flavor-selector .flavor-selector__toggle")
        ?.focus({ preventScroll: true });
    } catch (error) {
      console.error("Flavor selector navigation failed:", error);
      window.location.assign(destination.href);
    } finally {
      FlavorSelector.isNavigating = false;
      this.removeAttribute("data-loading");
      this.removeAttribute("aria-busy");
    }
  }

  async coverPage(color) {
    const transition = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg"
    );
    const wavePath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path"
    );

    transition.classList.add("flavor-page-transition");
    transition.setAttribute("aria-hidden", "true");
    transition.setAttribute("viewBox", "0 0 1 1");
    transition.setAttribute("preserveAspectRatio", "none");
    transition.setAttribute("focusable", "false");
    transition.style.setProperty("--flavor-transition-color", color);
    wavePath.classList.add("flavor-page-transition__wave");
    wavePath.setAttribute("d", "M0 0V1H0Z");
    transition.appendChild(wavePath);
    document.body.appendChild(transition);

    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    transition.classList.add("flavor-page-transition--covering");
    await this.animatePageTransition(wavePath, "cover");
    return transition;
  }

  async revealPage(transition) {
    const wavePath = transition.querySelector(".flavor-page-transition__wave");
    transition.classList.add("flavor-page-transition--revealing");
    await this.animatePageTransition(wavePath, "reveal");
    transition.remove();
  }

  animatePageTransition(path, direction) {
    if (!path) return Promise.resolve();

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reducedMotion) {
      path.setAttribute("d", this.pageTransitionWavePath(1, direction));
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const duration = 700;
      const startedAt = performance.now();

      const step = (now) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        path.setAttribute(
          "d",
          this.pageTransitionWavePath(easedProgress, direction)
        );

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(step);
    });
  }

  pageTransitionWavePath(value, direction) {
    const reveal = Math.max(0, Math.min(value, 1));
    const fadeProgress = Math.max(0, Math.min((reveal - 0.84) / 0.16, 1));
    const fade = 1 - Math.pow(1 - fadeProgress, 3);
    const amplitude = 1 - fade;
    const x0 = reveal - 0.014193 * amplitude;
    const c1x = reveal - 0.014073 * amplitude;
    const c2x = reveal + 0.041858 * amplitude;
    const c3x = reveal - 0.082988 * amplitude;
    const c4x = reveal - 0.207834 * amplitude;
    const c5x = reveal - 0.001454 * amplitude;
    const boundary = `L${x0} 0C${c1x} -0.000145 ${c2x} 0.198726 ${c3x} 0.5C${c4x} 0.801274 ${c5x} 1 ${c5x} 1`;

    if (direction === "reveal") {
      if (reveal === 0) return "M0 0H1V1H0Z";
      if (reveal === 1) return "M1 0V1H1Z";
      return `M1 0H${x0}C${c1x} -0.000145 ${c2x} 0.198726 ${c3x} 0.5C${c4x} 0.801274 ${c5x} 1 ${c5x} 1H1V0Z`;
    }

    if (reveal === 0) return "M0 0V1H0Z";
    return `M0 0${boundary}H0V0Z`;
  }

  destroyCurrentSliders(root) {
    root.querySelectorAll(".swiper-initialized").forEach((slider) => {
      slider.swiper?.destroy(true, true);
    });
  }

  removeDetachedModals(root) {
    const modalSelectors = new Set(
      Array.from(root.querySelectorAll("modal-opener[data-modal]"), (opener) =>
        opener.getAttribute("data-modal")
      )
    );

    modalSelectors.forEach((selector) => {
      const modal = document.querySelector(selector);
      if (!modal || root.contains(modal)) return;

      this.destroyCurrentSliders(modal);
      modal.remove();
    });
  }

  syncProductTheme(responseDocument) {
    const currentThemeData = document.querySelector("[data-custom-product-theme]");
    const nextThemeData = responseDocument.querySelector(
      "[data-custom-product-theme]"
    );
    const currentThemeStyles = document.querySelector(
      "[data-custom-product-theme-styles]"
    );
    const nextThemeStyles = responseDocument.querySelector(
      "[data-custom-product-theme-styles]"
    );

    this.replaceHeadElement(currentThemeData, nextThemeData);
    this.replaceHeadElement(currentThemeStyles, nextThemeStyles);

    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor && nextThemeData?.dataset.pageBackground) {
      themeColor.content = nextThemeData.dataset.pageBackground;
    }
  }

  replaceHeadElement(currentElement, nextElement) {
    if (nextElement) {
      const importedElement = document.importNode(nextElement, true);
      if (currentElement) {
        currentElement.replaceWith(importedElement);
      } else {
        document.head.appendChild(importedElement);
      }
    } else {
      currentElement?.remove();
    }
  }

  syncDocumentMetadata(responseDocument, destination, updateHistory) {
    document.title = responseDocument.title;

    const currentCanonical = document.querySelector('link[rel="canonical"]');
    const nextCanonical = responseDocument.querySelector('link[rel="canonical"]');
    if (currentCanonical && nextCanonical) {
      currentCanonical.href = nextCanonical.href;
    }

    const currentDescription = document.querySelector('meta[name="description"]');
    const nextDescription = responseDocument.querySelector(
      'meta[name="description"]'
    );
    if (currentDescription && nextDescription) {
      currentDescription.content = nextDescription.content;
    }

    if (updateHistory) {
      window.history.pushState({ flavorSelector: true }, "", destination.href);
    }
  }

  initializePageContent(root) {
    if (typeof ProductAdditionalService !== "undefined") {
      ProductAdditionalService.selectedServices = [];
    }

    root.querySelectorAll("script:not([src])").forEach((oldScript) => {
      const scriptType = oldScript.getAttribute("type");
      if (scriptType && scriptType !== "text/javascript" && scriptType !== "module") {
        return;
      }

      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attribute) => {
        newScript.setAttribute(attribute.name, attribute.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });

    root.querySelectorAll(".shopify-section").forEach((section) => {
      section.dispatchEvent(
        new CustomEvent("shopify:section:load", { bubbles: true })
      );
    });

    this.initializeProductGallery(root);

    if (window.Shopify?.PaymentButton) Shopify.PaymentButton.init();
    if (window.ProductModel) window.ProductModel.loadShopifyXR();
  }

  initializeProductGallery(root) {
    const productSection = root.querySelector(".product-section");
    if (!productSection) return;

    const mediaList = productSection.querySelector(".js-media-list");
    const mediaSublist = productSection.querySelector(".js-media-sublist");
    const productModalSelector = productSection
      .querySelector('modal-opener[data-modal^="#ProductModal-"]')
      ?.getAttribute("data-modal");
    const productModal = productModalSelector
      ? document.querySelector(productModalSelector)
      : null;
    const popupSlider = productModal?.querySelector(".js-popup-slider");

    if (
      mediaSublist &&
      !mediaSublist.swiper &&
      typeof subSliderInit === "function"
    ) {
      subSliderInit(productSection);
    }

    if (mediaList && !mediaList.swiper && typeof sliderInit === "function") {
      sliderInit(productSection);
    }

    if (
      popupSlider &&
      !popupSlider.swiper &&
      typeof popupSliderInit === "function"
    ) {
      popupSliderInit(productSection);
    }

    const updateSliders = () => {
      [mediaList, mediaSublist, popupSlider].forEach((slider) => {
        slider?.swiper?.update();
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateSliders();
        [mediaList, mediaSublist, popupSlider].forEach((slider) => {
          slider?.swiper?.slideTo(0, 0);
        });
      });
    });

    productSection.querySelectorAll(".product__media img").forEach((image) => {
      if (!image.complete) {
        image.addEventListener("load", updateSliders, { once: true });
      }
    });
  }
}

FlavorSelector.isNavigating = false;

if (!customElements.get("flavor-selector")) {
  customElements.define("flavor-selector", FlavorSelector);
}
